import React, { useCallback, useEffect, useMemo, useState } from 'react'
import MaterialTable from 'material-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMinusCircle, faPen } from '@fortawesome/free-solid-svg-icons'
import CustomAxios from '../../customComponents/customAxios'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { ROUTE_NAMES } from '../../utils/routes'
import DeleteModal from '../Modals/DeleteModal'
import { Reducers, ReducerUser } from '../../types/reducers'
import { usePrevious, useTablePagination } from '../../utils/hooks/common'
import { Network } from '../../types/data/system'
import LogoDefault from '../../resources/media/images/whitelabel/login-header.png'
import CustomAxiosImages from '../../customComponents/customAxiosImages'
import * as tableLocales from '../../resources/tableLocales/tableLocales'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { setUserDefaultTablePageSize } from '../../store/actions/user'
import { AvailableTablePageSizes } from '../../utils/enum/UserActivityTypes'
import { config } from '../../config/config'
import { COLORS } from '../../styles/constants'

interface Props {
    searchQuery: string
    onFinish?: () => void
}

const NetworksTable: React.FC<Props> = (props) => {
    const [selectedNetwork, setSelectedNetwork] = useState<Network | null>(null)
    const user = useSelector<Reducers, ReducerUser>((state) => state.user)
    const [pageSize, setPageSize] = useState(user.defaultTablePageSize)
    const [firstDataLoad, setFirstDataLoad] = useState(true)
    const [currentPage, setCurrentPage] = useState(0)
    const [totalElements, setTotalElements] = useState(0)
    const tableRef = React.useRef<any>()
    const navigate = useNavigate()
    const previousQuery = usePrevious(props.searchQuery)
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const currentPageFromURL = useTablePagination('page')

    useEffect(() => {
        handlePageDataUpdate()
    }, [])

    const handlePageDataUpdate = useCallback(() => {
        const currentPageValue = currentPageFromURL > 0 ? currentPageFromURL : 0
        if (currentPageValue * pageSize <= totalElements) {
            setCurrentPage(currentPageValue)
            resetData(currentPageValue)
        }
    }, [currentPageFromURL, totalElements, pageSize])

    const handleDataCount = (totCounter: number): number => {
        setTotalElements(totCounter)
        return totCounter
    }

    const removeHandler = async (network: Network) => {
        try {
            await CustomAxios.delete(`networks/network/${network.ID}`)
            if (network.logo) {
                const data = new FormData()
                data.append('table', 'networks')
                data.append('object_id', `${network.ID.toString()}-logo`)
                await CustomAxiosImages.delete('images', {
                    headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${user.token}` },
                    data,
                })
            }
            if (network.banner) {
                const data = new FormData()
                data.append('table', 'networks')
                data.append('object_id', `${network.ID.toString()}-banner`)
                await CustomAxiosImages.delete('images', {
                    headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${user.token}` },
                    data,
                })
            }
            resetData(currentPage, '', '', true)
            setSelectedNetwork(null)
        } catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        if (tableRef.current && (props.searchQuery.length > 2 || (previousQuery && previousQuery.length > 2))) {
            resetData(currentPage, '', '', true)
        }
    }, [props.searchQuery])

    const resetData = useCallback((page = 0, headerToSortBy = '', order = '', forceReload = false) => {
        if (page === currentPage && !forceReload) return
        tableRef.current.onQueryChange({ page, orderBy: headerToSortBy, orderDirection: order })
    }, [])

    const columns = useMemo(() => {
        const baseUrl = config.api.baseURL
        const columns = [
            {
                title: '',
                field: 'logo',
                render: (rowData: Network) => (
                    <div className="table-element-logo-container">
                        <img
                            src={
                                rowData.logo && typeof rowData.logo === 'string'
                                    ? `${baseUrl}/${rowData.logo}`
                                    : LogoDefault
                            }
                            alt={rowData.name}
                        />
                    </div>
                ),
            },
            {
                title: t('fields.name'),
                field: 'name',
                render: (rowData: Network) => (
                    <span
                        className="colorRed table-link-value"
                        onClick={() => navigate(`/${ROUTE_NAMES.NETWORKS}/${rowData?.ID}`, { state: { isView: true } })}
                    >
                        {rowData.name}
                    </span>
                ),
            },
            {
                title: t('fields.description'),
                field: 'description',
            },
        ]
        return columns
    }, [])

    const actions = useMemo(() => {
        const actions = [
            (rowData: Network) =>
                (user.idSysGrant === 0 || user.managedNetworks.find((networkId) => rowData.ID === networkId)) && {
                    icon: () => <FontAwesomeIcon icon={faPen} color={COLORS.palette.darkGrey} size="xs" />,
                    onClick: () => navigate(`/${ROUTE_NAMES.NETWORKS}/${rowData?.ID}`),
                    tooltip: t('common.edit'),
                },
            (rowData: Network) =>
                (user.idSysGrant === 0 || user.managedNetworks.find((networkId) => rowData.ID === networkId)) && {
                    icon: () => <FontAwesomeIcon icon={faMinusCircle} color={COLORS.palette.red} size="xs" />,
                    onClick: () => setSelectedNetwork(rowData),
                    tooltip: t('common.remove'),
                },
        ]
        return actions
    }, [])

    return (
        <>
            {selectedNetwork ? (
                <DeleteModal
                    deleteMessage={t('systemMessages.typeDeleteConfirmMessage', { type: selectedNetwork.name })}
                    onClose={() => setSelectedNetwork(null)}
                    onDelete={() => void removeHandler(selectedNetwork)}
                />
            ) : null}
            <MaterialTable
                columns={columns}
                tableRef={tableRef}
                data={async (query) =>
                    new Promise((resolve) => {
                        const newOffset = pageSize + pageSize * (query.page - 1)
                        let url = 'networks'
                        url += `?limit=${pageSize}`
                        url += `&offset=${newOffset}`
                        if (
                            query.orderBy !== undefined &&
                            query.orderBy.field !== undefined &&
                            (query.orderDirection as string) !== ''
                        ) {
                            url += `&sort_by=${query.orderBy.field.toString()}&order_by=${
                                query.orderDirection[0].toUpperCase() + query.orderDirection.slice(1)
                            }`
                        }
                        if (props.searchQuery && props.searchQuery.length > 2) {
                            url += `&query=${props.searchQuery}`
                        }
                        void CustomAxios.get(url).then((response) => {
                            resolve({
                                data: response.data.data,
                                page: query.page,
                                totalCount: handleDataCount(response.data.count),
                            })
                            if (firstDataLoad && props.onFinish) {
                                props.onFinish()
                                setFirstDataLoad(false)
                            }
                        })
                    })
                }
                actions={actions}
                title=""
                options={{
                    pageSize,
                    pageSizeOptions: AvailableTablePageSizes,
                    search: false,
                    actionsColumnIndex: 3,
                }}
                onChangePage={(pageIndex) => {
                    setCurrentPage(pageIndex)
                    navigate(`/${ROUTE_NAMES.NETWORKS}?page=${pageIndex}`)
                }}
                onChangeRowsPerPage={(pageSize) => {
                    dispatch(setUserDefaultTablePageSize(pageSize))
                    setPageSize(pageSize)
                    resetData()
                }}
                localization={tableLocales[user.language.split('-')[0]]}
            />
        </>
    )
}

export default NetworksTable
